import React, { useState } from 'react';
import PageTitle from '../common/PageTitle';
import FormInput from '../common/FormInput';
import { DealerAddressType, SelectOpt } from '../common/CommonTypes';
import { useTranslation } from 'react-i18next';
import SelectInput, { SelectInputHelper } from '../common/selectInput/SelectInput';
import { useNavigate } from 'react-router-dom';
import { RoutePaths, SelectOptionsYesNo } from '../../Constants/Common';
import { useSelectedProduct } from 'components/hooks/useSelectedProduct';
import './RepairRequestPreCheckPage.scss';
import DealerAddressLookup from 'components/common/dealerAddress/DealerAddressLookup';
import DealerAddress from 'components/common/dealerAddress/DealerAddress';
import ErrorSummary from '../common/ErrorSummary/ErrorSummary';

export type RepairRequestPreCheckDetails = {
    carDealerOption?: string;
    dealerSelection?: string;
};

const RepairRequestPreCheckPage = (): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [preCheckDetails, setPreCheckDetails] =
        useState<RepairRequestPreCheckDetails>({});
    const onChangeCarDealerOption = (selectedValue: string) => {
        setPreCheckDetails({
            ...preCheckDetails,
            carDealerOption: selectedValue
        });
    };

    const { product, productRoute } = useSelectedProduct();
    const [dealerAddress, setDealerAddress] = useState<DealerAddressType>({});
    const [formErrors, setFormError] = useState<RepairRequestPreCheckDetails>({});

    const errorlist = (): Array<string | undefined> => {
        const values = [
            formErrors?.carDealerOption,
            formErrors?.dealerSelection
        ];

        const errors = values.filter((error) => error !== undefined);

        return errors;
    };

    const isPageValid = (): boolean => {
        let validationError = false;
        const errors: RepairRequestPreCheckDetails = {};

        if (!SelectInputHelper.isValidValue(preCheckDetails.carDealerOption)) {
            errors.carDealerOption = t('dealer_option_is_required');
            validationError = true;
        }
        if (preCheckDetails.carDealerOption == SelectOptionsYesNo.No) {
            if (!SelectInputHelper.isValidValue(dealerAddress.locationCountry)) {
                errors.dealerSelection = t('country_is_required');
                validationError = true;
            }
        }

        if (SelectInputHelper.isValidValue(dealerAddress.locationCountry)) {
            if (!SelectInputHelper.isValidValue(dealerAddress.stateOrProvince) && dealerAddress.locationCountry !== 'Other') {
                errors.dealerSelection = t('stateorprovince_is_required');
                validationError = true;
            }
        }

        if (SelectInputHelper.isValidValue(dealerAddress.stateOrProvince)) {
            if (dealerAddress.dealerStreetAddress === undefined) {
                errors.dealerSelection = t('dealer_is_required');
                validationError = true;
            }
        }

        setFormError(errors);

        return !validationError;
    };

    const YesNoOptions: SelectOpt[] = [
        {
            id: SelectOptionsYesNo.Yes,
            text: t('option_yes'),
            value: SelectOptionsYesNo.Yes,
            disabled: false
        },
        {
            id: SelectOptionsYesNo.No,
            text: t('option_no'),
            value: SelectOptionsYesNo.No,
            disabled: false
        }
    ];

    const onBackButtonClick = (): void => {
        navigate(RoutePaths.Home);
    };

    const onContinueButtonClick = (): void => {
        if (isPageValid()) {
            event.preventDefault();

            const state = {
                product: product,
                selectedDealerAddress: dealerAddress
            };

            navigate(productRoute || '/', { state });
        }

    };

    const onDealerAddressUpdated = (dealerAddress: DealerAddressType): void => {
        setDealerAddress(dealerAddress);
    };

    return (
        <div>
            <div className='card page-content'>
                <PageTitle title={t('repair_request_pre_check_title')} />
                <div
                    className='card-body page-content-body'
                    id='car-dealer-section'>
                    <FormInput
                        id='car-dealer-question-forminput'
                        label={t('is_car_dealer_question')}
                        errorText={formErrors?.carDealerOption}>
                        <SelectInput
                            name='car-dealer-question'
                            loadingText={t('loading')}
                            value={preCheckDetails.carDealerOption}
                            onChange={onChangeCarDealerOption}
                            options={YesNoOptions}
                            forceDefault
                            defaultIfOneOption
                            addDefaultOption
                            defaultText={t('select_default')}
                        />
                        {preCheckDetails.carDealerOption ==
                            SelectOptionsYesNo.Yes && (
                            <div
                                className='alert alert-warning panel-selection-alert-container'
                                role='alert'>
                                {t('is_car_dealer_question_yes')}
                            </div>
                        )}
                        {preCheckDetails.carDealerOption ==
                            SelectOptionsYesNo.No && (
                            <>
                                <div
                                    className='alert alert-warning panel-selection-alert-container'
                                    role='alert'>
                                    {t('is_car_dealer_question_no')}
                                </div>
                            </>
                        )}
                    </FormInput>
                    {preCheckDetails.carDealerOption ==
                        SelectOptionsYesNo.No && (
                        <FormInput
                            id='find-dealer-forminput'
                            label={t('find_dealer')}
                            errorText={formErrors?.dealerSelection}>
                            <DealerAddressLookup
                                dealerAddress={dealerAddress}
                                onDealerAddressUpdated={onDealerAddressUpdated}
                            />
                            {dealerAddress &&
                                dealerAddress.dealerStreetAddress && (
                                    <DealerAddress address={dealerAddress} />
                                )}
                        </FormInput>
                    )}
                    <div className='bottom-navigation'>
                        <div className='card-footer page-content-footer'>
                            {formErrors && (
                                <ErrorSummary
                                    title={'Pre check errors'}
                                    errors={errorlist()}
                                    className='card-error'
                                />
                            )}
                        </div>
                        <button
                            className='btn btn-primary progress-button'
                            onClick={() => onContinueButtonClick()}>
                            {t('continue_with_the_request')}
                        </button>
                        <button
                            aria-label='back-button'
                            className='btn btn-secondary progress-button'
                            onClick={() => onBackButtonClick()}>
                            {t('back')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RepairRequestPreCheckPage;
